/**
 * CR-6449: loads the dropdown vocabularies for the Purchasing → ASN filter
 * panel and the raw lists the cascade hook narrows.
 *
 * Extracted from `ASNTab` unchanged in behaviour (CR-4580: no filter
 * persistence, options are fetched once per user role). The only functional
 * change is the warehouse option value: it is now the warehouse `code`, which
 * is what the backend `warehouse_code` filter matches — the id it used to
 * send never matched anything, so the Warehouse filter was inert.
 */
import { useEffect, useState } from 'react';
import { purchasingAPI, coreAPI, fetchAllPages, Site, CompanyGroup, Warehouse } from '../../../../lib/api';
import type { ASNCascadeFilterOptions } from './useASNFilterCascades';
import { ASN_COURIER_STATUS_OPTIONS, type ASNFilterOption } from './asnFilterOptions';
import { mergeCascadeSites, mergeDivisions, mergeSiteCountries } from './asnOptionMerge';
import { countryDisplayNames } from '../../../dashboard/dashboardFilterUtils';
import { useViewKey } from '../../../../contexts/ViewContext';

export interface ASNFilterOptionsState {
  siteOptions: ASNFilterOption[];
  warehouseOptions: ASNFilterOption[];
  supplierCountryOptions: ASNFilterOption[];
  supplierGroupOptions: ASNFilterOption[];
  commodityGroupOptions: ASNFilterOption[];
  commodityTypeOptions: ASNFilterOption[];
  divisionOptions: ASNFilterOption[];
  siteCountries: string[];
  cascadeFilterOptions: ASNCascadeFilterOptions | null;
  /**
   * CR-6441 / CR-6160: Courier Delivery Status choices as the backend
   * declares them; the static list stands in until the call answers. The
   * control itself is only rendered with the tracking module on.
   */
  courierStatusOptions: ASNFilterOption[];
}

interface LineFilterData {
  divisions?: Array<{ id: number; name: string }>;
  site_countries?: string[];
  supplier_countries?: string[];
  suppliers?: Array<{ id: number; name: string; group?: number | null; country?: string | null; customers?: number[] }>;
  customers?: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
  commodity_groups?: Array<{ id: number; name: string }>;
  commodity_types?: Array<{ id: number; name: string; commodity_group_id: number }>;
  /** CR-6449: PO delivery warehouses in scope, with the code the ASN filter matches. */
  warehouses?: Array<{ id: number; name: string; site?: number | null; code?: string | null }>;
}

interface WarehouseChoice { id: number; name: string; code: string; site: number | null }

/**
 * CR-6449: one warehouse list from two sources, keyed by code. Customers get
 * their own warehouses from the accessible endpoint; a supplier has none of
 * its own there, so its customers' delivery warehouses come from the PO-line
 * filter options instead. Either source alone leaves one role with an empty
 * dropdown.
 */
function mergeWarehouses(
  accessible: Warehouse[],
  fromLines: NonNullable<LineFilterData['warehouses']>,
): WarehouseChoice[] {
  const byCode = new Map<string, WarehouseChoice>();
  for (const w of accessible) {
    if (w.code) byCode.set(w.code, { id: w.id, name: w.name, code: w.code, site: w.site ?? null });
  }
  for (const w of fromLines) {
    if (w.code && !byCode.has(w.code)) byCode.set(w.code, { id: w.id, name: w.name, code: w.code, site: w.site ?? null });
  }
  return Array.from(byCode.values()).sort((a, b) => a.name.localeCompare(b.name));
}

type ASNFilterOptionsPayload = Awaited<ReturnType<typeof purchasingAPI.getASNFilterOptions>>['data'];

/** ISO code → display name, as the Dashboard shows it; unknown codes pass through. */
const countryOption = (code: string): ASNFilterOption => ({ value: code, label: countryDisplayNames[code] || code });

export function useASNFilterOptions(
  isSupplier: boolean,
  isHelixUser: boolean,
): ASNFilterOptionsState {
  // CR-6441 / CR-6160: the courier vocabulary comes from the ASN endpoint so
  // the dropdown can never carry a value the model rejects; the static list
  // stands in until the call answers.
  const [courierStatusOptions, setCourierStatusOptions] = useState<ASNFilterOption[]>(ASN_COURIER_STATUS_OPTIONS);

  const [siteOptions, setSiteOptions] = useState<ASNFilterOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<ASNFilterOption[]>([]);
  const [supplierCountryOptions, setSupplierCountryOptions] = useState<ASNFilterOption[]>([]);
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<ASNFilterOption[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<ASNFilterOption[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<ASNFilterOption[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<ASNFilterOption[]>([]);
  const [siteCountries, setSiteCountries] = useState<string[]>([]);
  const [cascadeFilterOptions, setCascadeFilterOptions] = useState<ASNCascadeFilterOptions | null>(null);

  const viewKey = useViewKey(); // supplier options follow the site switcher
  useEffect(() => {
    Promise.all([
      coreAPI.getAccessibleSites().catch(() => ({ data: [] })),
      coreAPI.getAccessibleWarehouses().catch(() => ({ data: [] as Warehouse[] })),
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      // The unfiltered list is Helix-only (403 otherwise): customers read the
      // scoped for-filter endpoint, suppliers have no supplier-group filter.
      isSupplier
        ? Promise.resolve([] as CompanyGroup[])
        : isHelixUser
          ? fetchAllPages<CompanyGroup>((page) => coreAPI.getCompanyGroups({ page_size: 100, page, company_type: 'supplier' })).catch(() => [] as CompanyGroup[])
          : coreAPI.getSupplierGroupsForFilter().then((res) => res.data.results || []).catch(() => [] as CompanyGroup[]),
      // CR-6449: Fetch supplier sites for country extraction
      coreAPI.getSupplierSitesForFilter().catch(() => ({ data: { results: [] } })),
      // CR-6441: Fetch divisions, commodity groups/types, and countries from line filter options
      // This is the same endpoint used by Forecast tab and returns proper commodity data
      purchasingAPI.getLineFilterOptions().catch(() => ({ data: {} })),
      // CR-6441 re-test: the ASN endpoint's own vocabularies — courier
      // statuses and the suppliers (with the sites they ship to) scoped like
      // the ASN list. Always requested; the courier list is only rendered
      // with the tracking module, the suppliers feed every view.
      purchasingAPI.getASNFilterOptions().catch(() => ({ data: {} as ASNFilterOptionsPayload })),
    ]).then(([sitesRes, warehousesRes, companyGroups, suppliersRes, lineFilterRes, asnOptionsRes]) => {
      const asnOptions = (asnOptionsRes.data || {}) as Partial<ASNFilterOptionsPayload>;
      const statuses = asnOptions.courier_delivery_statuses;
      if (Array.isArray(statuses) && statuses.length > 0) {
        setCourierStatusOptions(statuses.map((s) => ({ value: s.value, label: s.label })));
      }

      const sites = (Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || []) as Site[];
      setSiteOptions(sites.map((s) => ({ value: String(s.id), label: s.name })));

      // CR-6441: Extract data from line filter options (same endpoint as Forecast tab)
      const lineData = lineFilterRes.data as LineFilterData;

      // CR-6449: Warehouse options — value is the code the backend filter matches.
      const accessibleWarehouses: Warehouse[] = Array.isArray(warehousesRes.data) ? warehousesRes.data : [];
      const warehouses = mergeWarehouses(accessibleWarehouses, lineData.warehouses || []);
      setWarehouseOptions(warehouses.map((w) => ({ value: w.code, label: w.name })));

      const suppliersData = (suppliersRes.data as { results?: Array<{ id: number; name: string; country?: string; group?: number | null; customers?: number[] }> })?.results || [];

      // CR-6441 re-test (7 Sep): prefer the ASN endpoint's suppliers — scoped
      // to every site the user can view, with the Site → Supplier mapping. The
      // line filter options only know the home company (a sibling site got an
      // empty Supplier Site list); the for-filter list has no site mapping.
      const asnSuppliers = asnOptions.suppliers || [];
      const lineSuppliers = lineData.suppliers || [];
      const allSuppliers = asnSuppliers.length > 0
        ? asnSuppliers
        : lineSuppliers.length > 0 ? lineSuppliers : suppliersData;

      // CR-6449: Supplier Country choices — every country a listed supplier is
      // in, shown by display name as on the Dashboard (gap 3, Sep re-test).
      const uniqueSupplierCountries = Array.from(new Set(
        [...allSuppliers, ...suppliersData].map((s) => s.country).filter(Boolean),
      )) as string[];
      uniqueSupplierCountries.sort();
      setSupplierCountryOptions(uniqueSupplierCountries.map(countryOption));

      // CR-6441 re-test (7 Sep): the line filter options are scoped to the
      // user's home company, so on their own they offered one site and one
      // division. Union them with the accessible sites (the pre-rework source
      // of the Site dropdown) so every site the user can view is offered and
      // the Division → Site cascade has something to narrow.
      const lineSites = (lineData.customers || []).map((c) => ({
        id: c.id,
        name: c.name,
        group: c.group ?? null,
        country: c.country ?? null,
      }));
      const cascadeSites = mergeCascadeSites(lineSites, sites);
      const divisions = mergeDivisions(lineData.divisions || [], sites);
      const uniqueSiteCountries = mergeSiteCountries(lineData.site_countries || [], cascadeSites);
      setSiteCountries(uniqueSiteCountries);
      setDivisionOptions(divisions.map((d) => ({ value: String(d.id), label: d.name })));

      // CR-6441: Use commodity groups/types from lineFilterOptions (same as Forecast tab)
      const groups = lineData.commodity_groups || [];
      setCommodityGroupOptions(groups.map((g) => ({ value: String(g.id), label: g.name })));

      const types = lineData.commodity_types || [];
      setCommodityTypeOptions(types.map((t) => ({ value: String(t.id), label: t.name })));

      setSupplierGroupOptions(companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name })));

      // CR-6441: Build cascade filter options
      setCascadeFilterOptions({
        suppliers: allSuppliers.map((s) => ({
          id: s.id,
          name: s.name,
          group: s.group ?? null,
          customers: s.customers ?? [],
          country: s.country ?? null,
        })),
        commodityGroups: groups.map((g) => ({ id: g.id, name: g.name })),
        // Note: lineFilterOptions uses commodity_group_id instead of commodity_group
        commodityTypes: types.map((t) => ({
          id: t.id,
          name: t.name,
          commodity_group: t.commodity_group_id ?? 0,
        })),
        supplierGroups: companyGroups.map((g: CompanyGroup) => ({ id: g.id, name: g.name })),
        sites: cascadeSites,
        // CR-6449: warehouses with their site for the Site → Warehouse cascade
        warehouses,
        divisions,
        siteCountries: uniqueSiteCountries,
        supplierCountries: uniqueSupplierCountries,
      });
    });
  }, [isSupplier, isHelixUser, viewKey]);

  return {
    siteOptions,
    warehouseOptions,
    supplierCountryOptions,
    supplierGroupOptions,
    commodityGroupOptions,
    commodityTypeOptions,
    divisionOptions,
    siteCountries,
    cascadeFilterOptions,
    courierStatusOptions,
  };
}
